// Keep this independent of application modules: GlobalState starts auth on import.
// Change this only for an intentional browser-data migration, never for every build.
export const AUTH_RESET_VERSION = '2026-10-08';
export const AUTH_RESET_KEY = 'awareAuthResetVersion';
const PRESERVED_KEYS = new Set(['awareTheme', 'globalState', AUTH_RESET_KEY]);

function resetTabStorage() {
  if (window.sessionStorage.getItem(AUTH_RESET_KEY) === AUTH_RESET_VERSION) return;
  window.sessionStorage.clear();
  window.sessionStorage.setItem(AUTH_RESET_KEY, AUTH_RESET_VERSION);
}

async function resetBrowserAuth() {
  // Read inside the cross-tab lock so another tab's completed reset wins.
  if (window.localStorage.getItem(AUTH_RESET_KEY) === AUTH_RESET_VERSION) {
    resetTabStorage();
    return;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch('/api/auth/reset', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'X-AWARE-Auth-Reset': '1' },
      signal: controller.signal
    });
    // Also reject an old server's HTML fallback or an unexpected response.
    if (!response.ok || (await response.json())?.reset !== true) {
      throw new Error('Sign-in reset unavailable');
    }
  } finally {
    window.clearTimeout(timeout);
  }

  // Remove individually: the two preferences are never deleted or rewritten.
  for (let index = window.localStorage.length - 1; index >= 0; index--) {
    const key = window.localStorage.key(index);
    if (key !== null && !PRESERVED_KEYS.has(key)) window.localStorage.removeItem(key);
  }
  resetTabStorage();
  // Only mark complete after server revocation and browser cleanup both succeed.
  window.localStorage.setItem(AUTH_RESET_KEY, AUTH_RESET_VERSION);
}

export async function prepareBrowserAuth() {
  // Serializes first visits in modern HTTPS browsers, including separate tabs.
  if (navigator.locks) {
    await navigator.locks.request(AUTH_RESET_KEY, resetBrowserAuth);
  } else {
    await resetBrowserAuth();
  }
}
